import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@utils/cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 text-center text-md font-semibold",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow hover:bg-black/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border-2 border-azure-radiance-600 bg-white text-azure-radiance-600 shadow-sm hover:bg-azure-radiance-600 hover:text-white rounded-2xl py-4 px-3",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        accent: "bg-accent text-accent-foreground shadow-sm hover:bg-accent/90",
        accentOutline:
          "border border-accent text-accent bg-transparent shadow-sm hover:bg-accent/90 hover:text-accent-foreground",
        teamSwitcher:
          "bg-transparent hover:bg-white hover:text-foreground h-9 w-9 rounded-full",
        bigBlue:
          "bg-accent text-accent-foreground shadow-sm hover:bg-accent/95 text-md rounded-sm px-7 py-5",
        "tab-active": "bg-secondary text-black rounded-sm",
        tab: "bg-transparent text-secondary-foreground/20 rounded-sm hover:bg-secondary hover:text-secondary-foreground",
        handle:
          "text-primary underline-offset-4 hover:bg-gray-200 hover:cursor-grab",
        builderButton: "bg-white border border-gray-100",
        filled:
          "bg-azure-radiance-600 text-white shadow-sm hover:bg-white hover:text-azure-radiance-600 border-2 border-transparent hover:border-azure-radiance-600 rounded-2xl py-4 px-3",
        dropdown:
          "rounded-lg border border-[#83888D] hover:border-azure-radiance-600 bg-white p-2.5 text-[#6D7278] leading-trim-both text-edge-cap text-ellipsis font-instrument text-sm font-medium leading-6",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
        tab: "text-base px-2 py-1",
        handle: "rounded-none p-1",
        builderButton: "rounded px-1 py-1",
        giant: "h-11 rounded-2xl py-2 px-2",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
